import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../providers/NotificationProvider';
import { createAIJob, listAIJobs, processAIJob } from '../lib/aiQueue';
import type { AIJob, AIAnalysisResult, AIReportResult } from '../lib/aiQueue';
import { listAIReports, getReportFreshness, getReportCounts, type PersistedAIReport, type ReportFreshness } from '../lib/analytics';
import { listUsers, type UserDto } from '../lib/users';
import { DEFAULT_COMPANY_ID } from '../lib/config';
import { printDocument, downloadJSON, buildReportPrintHTML, buildAnalysisPrintHTML } from '../lib/exportUtils';
import { Search, FileText, ChevronDown, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/reports')({
  component: ReportsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    employee: typeof search['employee'] === 'string' ? search['employee'] : undefined,
    period:   typeof search['period']   === 'string' ? search['period']   : undefined,
  }),
});

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden">
      <div
        className="h-1.5 rounded-full bg-stone-800 transition-all duration-300 ease-out"
        style={{ width: `${Math.min(100, value)}%` }}
      />
    </div>
  );
}

function StatusDot({ status }: { status: AIJob['status'] }) {
  const styles: Record<AIJob['status'], string> = {
    pending:    'bg-stone-300',
    processing: 'bg-yellow-400 animate-pulse',
    completed:  'bg-green-500',
    failed:     'bg-red-500',
  };
  return <span className={cn('w-2 h-2 rounded-full shrink-0 inline-block', styles[status])} />;
}

function ReportExportDropdown({ result }: { result: AIReportResult }) {
  const [open, setOpen] = useState(false);
  const period = (result as { period?: string }).period ?? 'report';
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs px-3 py-1.5 border border-stone-200 text-stone-600 rounded-md hover:bg-stone-50 flex items-center gap-1"
      >
        Export <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-stone-200 rounded-lg shadow-lg z-20 py-1">
          <button
            type="button"
            onClick={() => { printDocument(buildReportPrintHTML(result), result.title); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-stone-700 hover:bg-stone-50"
          >
            Export as PDF
          </button>
          <button
            type="button"
            onClick={() => { downloadJSON(result, `ai-report-${period}.json`); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-stone-700 hover:bg-stone-50"
          >
            Export as JSON
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Freshness banner ─────────────────────────────────────────────────────────

function FreshnessBanner({
  freshness,
  freshLoading,
  onRegenerate,
  generating,
}: {
  freshness: ReportFreshness | null;
  freshLoading: boolean;
  onRegenerate: () => void;
  generating: boolean;
}) {
  if (freshLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border bg-stone-50 animate-pulse">
        <div className="w-4 h-4 rounded-full bg-stone-200" />
        <div className="h-3 bg-stone-200 rounded w-48" />
      </div>
    );
  }
  if (!freshness || !freshness.hasReport) return null;

  if (!freshness.isStale) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-green-200 bg-green-50 text-green-800">
        <CheckCircle2 className="w-4 h-4 shrink-0 text-green-600" />
        <span className="text-xs flex-1">
          Report reflects current data — no changes to metrics, psychometrics or 9-box position since generation.
        </span>
      </div>
    );
  }

  // Stale
  const changed = freshness.changedSince.length > 0
    ? freshness.changedSince.join(', ')
    : 'underlying data';
  const since = freshness.lastReportDate
    ? new Date(freshness.lastReportDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : 'last generation';

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50">
      <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-amber-900">Data changed since {since}</p>
        <p className="text-xs text-amber-700 mt-0.5">
          Changes detected in: <span className="font-medium">{changed}</span>.
          Regenerate to reflect the latest data.
        </p>
      </div>
      <button
        type="button"
        onClick={onRegenerate}
        disabled={generating}
        className="shrink-0 flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 font-medium transition-colors"
      >
        <RefreshCw className="w-3 h-3" />
        {generating ? 'Generating…' : 'Regenerate'}
      </button>
    </div>
  );
}

// ─── Report view ──────────────────────────────────────────────────────────────

function ReportView({ result }: { result: AIReportResult }) {
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-stone-900">{result.title}</h3>
          <p className="text-xs text-stone-500 mt-0.5">
            Generated {new Date(result.generatedAt).toLocaleString()} · Confidence:{' '}
            {Math.round(result.confidenceScore * 100)}%
          </p>
        </div>
        <ReportExportDropdown result={result} />
      </div>
      <div className="bg-stone-50 border rounded-lg p-4">
        <p className="text-sm font-medium text-stone-700 mb-1">Executive Summary</p>
        <p className="text-sm text-stone-600 leading-relaxed">{result.summary}</p>
      </div>
      <div className="space-y-4">
        {result.sections.map((section) => (
          <div key={section.heading} className="border-l-2 border-stone-200 pl-4">
            <h4 className="text-sm font-semibold text-stone-800 mb-1">{section.heading}</h4>
            <p className="text-sm text-stone-600 leading-relaxed">{section.content}</p>
          </div>
        ))}
      </div>
      {result.recommendations.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-stone-800 mb-2">Recommendations</p>
          <ul className="space-y-1">
            {result.recommendations.map((rec, i) => (
              <li key={i} className="flex gap-2 text-sm text-stone-600">
                <span className="text-stone-400 select-none mt-0.5">→</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AnalysisExportDropdown({ result, employeeName }: { result: AIAnalysisResult; employeeName?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs px-3 py-1.5 border border-stone-200 text-stone-600 rounded-md hover:bg-stone-50 flex items-center gap-1"
      >
        Export <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-stone-200 rounded-lg shadow-lg z-20 py-1">
          <button
            type="button"
            onClick={() => { printDocument(buildAnalysisPrintHTML(result, employeeName), `${employeeName ?? 'analysis'} — Analysis`); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-stone-700 hover:bg-stone-50"
          >
            Export as PDF
          </button>
          <button
            type="button"
            onClick={() => { downloadJSON(result, `ai-analysis-${employeeName?.replace(/\s+/g, '-') ?? 'result'}.json`); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-stone-700 hover:bg-stone-50"
          >
            Export as JSON
          </button>
        </div>
      )}
    </div>
  );
}

function AnalysisView({ result, employeeName }: { result: AIAnalysisResult; employeeName?: string }) {
  const PRIORITY_STYLES = {
    high:   'bg-red-100 text-red-700 border border-red-200',
    medium: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    low:    'bg-green-50 text-green-700 border border-green-200',
  };
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-bold text-stone-900">Employee Analysis</h3>
          <p className="text-xs text-stone-500 mt-0.5">Analyzed {new Date(result.analyzedAt).toLocaleString()}</p>
        </div>
        <AnalysisExportDropdown result={result} employeeName={employeeName} />
      </div>
      <div className="flex gap-4 flex-wrap">
        {(['developmentPriority', 'retentionRisk'] as const).map((key) => (
          <span key={key} className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-stone-500">
              {key === 'developmentPriority' ? 'Dev Priority' : 'Retention Risk'}:
            </span>
            <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', PRIORITY_STYLES[result[key]])}>
              {result[key]}
            </span>
          </span>
        ))}
      </div>
      {result.insights.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-stone-800 mb-2">Insights</p>
          <ul className="space-y-1.5">
            {result.insights.map((insight, i) => (
              <li key={i} className="flex gap-2 text-sm text-stone-600">
                <span className="text-stone-400 select-none mt-0.5 shrink-0">→</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {result.riskFlags.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-amber-800 mb-2">Risk Flags</p>
          <ul className="space-y-1">
            {result.riskFlags.map((flag, i) => (
              <li key={i} className="flex gap-2 text-sm text-amber-700">
                <span className="select-none shrink-0">⚠</span>
                <span>{flag}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ReportsPage() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const { employee: prefilledEmployee, period: prefilledPeriod } = Route.useSearch();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const isManager = user?.role === 'manager';

  // Employees
  const [employees, setEmployees] = useState<UserDto[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');

  // Reports & jobs
  const [jobs, setJobs] = useState<AIJob[]>([]);
  const [persistedReports, setPersistedReports] = useState<PersistedAIReport[]>([]);
  const [persistedLoading, setPersistedLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Freshness
  const [freshness, setFreshness] = useState<ReportFreshness | null>(null);
  const [freshnessLoading, setFreshnessLoading] = useState(false);

  // Per-employee report count (lazy, populated after each employee is loaded)
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});

  // Generate form
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [jobType, setJobType] = useState<'report' | 'analysis'>('report');
  const [period, setPeriod] = useState(prefilledPeriod ?? 'Q1 2026');
  const [showGenerateForm, setShowGenerateForm] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Filtered employee list — managers auto-scoped to their division
  const filteredEmployees = useMemo(() => {
    let list = employees;
    if (isManager && user?.divisionId) {
      list = list.filter((e) => e.divisionId === user.divisionId);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) => e.name.toLowerCase().includes(q));
    }
    return list;
  }, [employees, isManager, user?.divisionId, search]);

  const selectedEmployee = employees.find((e) => e.id === selectedUserId) ?? null;

  // Effect 1: load employees
  useEffect(() => {
    let cancelled = false;
    listUsers(companyId, { isActive: true })
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          const users = res.data.users;
          setEmployees(users);
          if (prefilledEmployee) {
            const match = users.find(
              (u) => u.name.toLowerCase() === prefilledEmployee.toLowerCase(),
            );
            if (match) setSelectedUserId(match.id);
          }
        }
      })
      .finally(() => { if (!cancelled) setEmployeesLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, prefilledEmployee]);

  // Effect 1b: preload report counts for ALL employees upfront (one query, no clicking needed)
  useEffect(() => {
    let cancelled = false;
    getReportCounts(companyId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setReportCounts(res.data.counts);
      }
    });
    return () => { cancelled = true; };
  }, [companyId]);

  // Effect 2: load in-memory queue jobs
  useEffect(() => {
    let cancelled = false;
    listAIJobs().catch(() => null).then((res) => {
      if (cancelled) return;
      if (res?.success) setJobs(res.data?.jobs ?? []);
    });
    return () => {
      cancelled = true;
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Effect 3: load persisted reports + freshness for selected employee
  useEffect(() => {
    if (!selectedUserId) {
      setPersistedReports([]);
      setSelectedId(null);
      setFreshness(null);
      return;
    }
    let cancelled = false;
    setPersistedLoading(true);
    setFreshnessLoading(true);

    // Load reports and freshness in parallel
    Promise.all([
      listAIReports(companyId, selectedUserId),
      getReportFreshness(companyId, selectedUserId),
    ]).then(([reportsRes, freshRes]) => {
      if (cancelled) return;

      if (reportsRes.success && reportsRes.data) {
        const reports = reportsRes.data.reports;
        setPersistedReports(reports);
        // Track report count per employee for the left rail badge
        setReportCounts((prev) => ({ ...prev, [selectedUserId]: reports.length }));
        // Auto-select newest persisted report
        if (reports.length > 0) setSelectedId(reports[0].id);
        else setSelectedId(null);
      }
      setPersistedLoading(false);

      if (freshRes.success && freshRes.data) setFreshness(freshRes.data);
      setFreshnessLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setPersistedLoading(false);
        setFreshnessLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [companyId, selectedUserId]);

  function startPolling(jobId: string, employeeName?: string) {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      const response = await processAIJob(jobId).catch(() => null);
      if (response?.success && response.data?.job) {
        const updated = response.data.job;
        setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
        setSelectedId(updated.id);
        if (updated.status === 'completed') {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setGenerating(false);
          setShowGenerateForm(false);
          // Refresh freshness after successful generation
          getReportFreshness(companyId, selectedUserId).then((res) => {
            if (res.success && res.data) setFreshness(res.data);
          });
          // Refresh persisted reports list
          listAIReports(companyId, selectedUserId).then((res) => {
            if (res.success && res.data) {
              setPersistedReports(res.data.reports);
              setReportCounts((prev) => ({ ...prev, [selectedUserId]: res.data!.reports.length }));
            }
          });
          addNotification({ type: 'success', title: 'Report Complete', message: `AI report for ${employeeName ?? 'employee'} generated.` });
        } else if (updated.status === 'failed') {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setGenerating(false);
          addNotification({ type: 'error', title: 'Report Failed', message: `Failed to generate for ${employeeName ?? 'employee'}.` });
        }
      }
    }, 3000);
  }

  async function handleGenerate(e?: React.FormEvent) {
    e?.preventDefault();
    if (!selectedUserId) return;
    if (generating) return; // re-entry guard — prevent double-click / rapid calls

    // Pre-check: employee must have at least some performance data before we waste an LLM call
    const snap = freshness?.dataSnapshot;
    const metricCount = snap?.metricCount ?? 0;
    const psychCount = snap?.psychCount ?? 0;
    if (metricCount === 0 && psychCount === 0) {
      const name = selectedEmployee?.name ?? 'This employee';
      toast.error('No data to generate a report from', {
        description: `${name} has no recorded metrics or assessment results. Complete at least one assessment or record a performance metric first.`,
        duration: 8000,
      });
      return;
    }

    setError('');
    setGenerating(true);
    const employeeName = selectedEmployee?.name ?? 'Employee';
    const createResponse = await createAIJob(jobType, {
      userId: selectedUserId, employeeName, period,
      requestedBy: user?.name ?? 'HR', companyId,
    }).catch(() => null);
    if (!createResponse?.success || !createResponse.data?.job) {
      const msg = (createResponse as any)?.error ?? 'Failed to create report job';
      const code = (createResponse as any)?.code ?? '';
      setGenerating(false);
      // job_in_progress: surface as toast (not form error — generate form may be hidden)
      if (code === 'job_in_progress' || msg.includes('already being generated')) {
        toast.info('Already generating', { description: 'A report is already in progress for this employee. Please wait for it to finish.' });
        // Still track the existing in-flight job so the UI shows its progress
        const existingJob = (createResponse as any)?.data?.job;
        if (existingJob) {
          setJobs((prev) => prev.some((j) => j.id === existingJob.id) ? prev : [existingJob, ...prev]);
          setSelectedId(existingJob.id);
          startPolling(existingJob.id, employeeName);
        }
        return;
      }
      // cooldown / unchanged: toast + form error
      const friendlyMsg = msg.includes('cooldown') || msg.includes('24') || msg.includes('unchanged')
        ? `⏱ ${msg}`
        : msg;
      toast.error('Cannot generate', { description: friendlyMsg });
      setError(friendlyMsg);
      return;
    }
    const job = createResponse.data.job;
    setJobs((prev) => [job, ...prev]);
    setSelectedId(job.id);
    startPolling(job.id, employeeName);
  }

  // Resolve active display
  const activeJob = selectedId ? jobs.find((j) => j.id === selectedId) ?? null : null;
  const activePersistedReport = selectedId
    ? persistedReports.find((r) => r.id === selectedId) ?? null
    : persistedReports[0] ?? null;
  const effectiveResult = activeJob ? null : activePersistedReport;

  // Derived: true if ANY job for this user is currently pending or processing.
  // Used to disable all generate/regenerate buttons — prevents duplicate submissions.
  const isGenerating =
    generating ||
    jobs.some(
      (j) =>
        (j.payload as Record<string, string>)['userId'] === selectedUserId &&
        (j.status === 'pending' || j.status === 'processing'),
    );

  // History = active (pending/processing) queue jobs for this user + all persisted reports.
  // Completed/failed jobs are excluded — they either resulted in a persisted report (which
  // already appears below) or failed silently. Showing them would create phantom duplicates
  // in the history list.
  const historyItems = [
    ...jobs.filter((j) => {
      if ((j.payload as Record<string, string>)['userId'] !== selectedUserId) return false;
      return j.status === 'pending' || j.status === 'processing';
    }),
    ...persistedReports,
  ];

  // Current data hash for comparing history entries
  const currentDataHash = freshness?.currentDataHash ?? null;

  return (
    <ProtectedRoute minimumRole="manager">
      <div className="flex h-[calc(100vh-8rem)] -mx-4 sm:-mx-6 lg:-mx-8 -mt-6 sm:-mt-8">

        {/* ── Left rail: employee list ─────────────────────────────── */}
        <div className="w-56 shrink-0 border-r flex flex-col bg-card hidden sm:flex">
          <div className="px-3 py-3 border-b">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Employees</h2>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-6 pr-2 py-1.5 text-xs bg-muted border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {employeesLoading ? (
              <div className="space-y-1 px-2 py-2">
                {[1,2,3,4].map((i) => <div key={i} className="h-9 bg-muted rounded-lg animate-pulse" />)}
              </div>
            ) : filteredEmployees.length === 0 ? (
              <p className="text-xs text-muted-foreground px-3 py-4">No employees found</p>
            ) : (
              filteredEmployees.map((emp) => {
                const count = reportCounts[emp.id];
                const initials = emp.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => { setSelectedUserId(emp.id); setShowGenerateForm(false); }}
                    className={cn(
                      'w-full text-left px-2 py-2 rounded-lg transition-colors flex items-center gap-2',
                      selectedUserId === emp.id
                        ? 'bg-stone-100 text-stone-900 mx-1'
                        : 'hover:bg-muted text-muted-foreground hover:text-foreground mx-1',
                    )}
                    style={{ width: 'calc(100% - 8px)' }}
                  >
                    <div className="w-6 h-6 rounded-full bg-stone-200 text-stone-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                      {initials}
                    </div>
                    <span className="text-xs font-medium truncate flex-1">{emp.name}</span>
                    {/* Report count badge — shown once loaded, honest about actual count */}
                    {count !== undefined && count > 0 && (
                      <span className="text-[10px] text-muted-foreground shrink-0 font-medium">
                        {count}
                      </span>
                    )}
                    {count === 0 && selectedUserId === emp.id && (
                      <span className="text-[10px] text-muted-foreground/50 shrink-0">0</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Main content area ────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Top toolbar */}
          <div className="px-5 py-3 border-b bg-card flex items-center gap-3 flex-wrap shrink-0">
            <div>
              <h1 className="text-sm font-semibold text-foreground">
                {selectedEmployee ? `${selectedEmployee.name} — AI Reports` : 'AI Reports'}
              </h1>
              {selectedEmployee && (
                <p className="text-xs text-muted-foreground">{selectedEmployee.email}</p>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              {selectedUserId && (
                <button
                  type="button"
                  onClick={() => setShowGenerateForm((v) => !v)}
                  disabled={isGenerating}
                  className="text-xs px-3 py-1.5 bg-stone-900 text-white rounded-md hover:bg-stone-700 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isGenerating ? 'Generating…' : showGenerateForm ? 'Cancel' : 'Generate New'}
                </button>
              )}
            </div>
          </div>

          {/* Mobile: employee selector */}
          <div className="sm:hidden px-4 py-2 border-b">
            <select
              className="w-full px-3 py-2 border rounded-md text-sm bg-white"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">Select employee…</option>
              {filteredEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">

            {/* Freshness banner — hidden while a job is actively running */}
            {selectedUserId && !persistedLoading && !isGenerating && (
              <FreshnessBanner
                freshness={freshness}
                freshLoading={freshnessLoading}
                onRegenerate={() => handleGenerate()}
                generating={isGenerating}
              />
            )}

            {/* Generate form — shown only when triggered */}
            {showGenerateForm && selectedUserId && (
              <div className="bg-stone-50 border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-stone-800 mb-3">Generate Report</h3>
                <form onSubmit={handleGenerate} className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-stone-600">Type</label>
                    <select
                      className="px-3 py-1.5 border rounded-md text-sm bg-white"
                      value={jobType}
                      onChange={(e) => setJobType(e.target.value as 'report' | 'analysis')}
                    >
                      <option value="report">Full Report</option>
                      <option value="analysis">Risk & Insight Analysis</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-stone-600">Period</label>
                    <input
                      className="px-3 py-1.5 border rounded-md text-sm"
                      placeholder="e.g. Q1 2026"
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isGenerating}
                    className="px-4 py-1.5 text-sm bg-stone-900 text-white rounded-md disabled:bg-stone-300 hover:bg-stone-700 font-medium"
                  >
                    {isGenerating ? 'Generating…' : 'Generate'}
                  </button>
                </form>
                {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
              </div>
            )}

            {/* No employee selected */}
            {!selectedUserId && (
              <div className="flex flex-col items-center justify-center h-64 text-stone-400 space-y-2">
                <FileText className="w-10 h-10 opacity-30" />
                <p className="text-sm">Select an employee to view their reports</p>
              </div>
            )}

            {/* Loading */}
            {selectedUserId && persistedLoading && (
              <div className="space-y-3 animate-pulse">
                <div className="h-4 bg-stone-100 rounded w-1/2" />
                <div className="h-3 bg-stone-100 rounded w-3/4" />
                <div className="h-3 bg-stone-100 rounded w-2/3" />
              </div>
            )}

            {/* Active job: processing */}
            {activeJob && activeJob.status === 'processing' && (
              <div className="space-y-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <StatusDot status="processing" />
                  <span className="text-sm text-yellow-800 font-medium">Generating report…</span>
                  <span className="text-xs text-yellow-600 ml-auto">{activeJob.progress}%</span>
                </div>
                <ProgressBar value={activeJob.progress} />
                <div className="animate-pulse space-y-2 pt-1">
                  <div className="h-3 bg-yellow-100 rounded w-3/4" />
                  <div className="h-3 bg-yellow-100 rounded w-1/2" />
                </div>
              </div>
            )}

            {activeJob && activeJob.status === 'pending' && (
              <div className="flex items-center gap-2 p-3 bg-stone-50 border rounded-lg">
                <StatusDot status="pending" />
                <span className="text-sm text-stone-600">Queued — waiting to process</span>
              </div>
            )}

            {activeJob && activeJob.status === 'failed' && (
              <div className="text-sm text-red-600 p-3 bg-red-50 border border-red-200 rounded-lg">
                Report generation failed: {activeJob.error ?? 'Unknown error'}
              </div>
            )}

            {/* Completed job result */}
            {activeJob && activeJob.status === 'completed' && activeJob.result && (
              (activeJob.result as AIReportResult).type === 'report'
                ? <ReportView result={activeJob.result as AIReportResult} />
                : <AnalysisView result={activeJob.result as AIAnalysisResult} employeeName={selectedEmployee?.name} />
            )}

            {/* Persisted report */}
            {!activeJob && effectiveResult && (
              <ReportView result={effectiveResult} />
            )}

            {/* Empty state */}
            {selectedUserId && !persistedLoading && !activeJob && !effectiveResult && !generating && (
              <div className="flex flex-col items-center justify-center h-48 text-stone-400 space-y-3">
                <FileText className="w-8 h-8 opacity-30" />
                <p className="text-sm font-medium text-stone-600">No reports for {selectedEmployee?.name}</p>
                <p className="text-xs text-stone-400">Click "Generate New" above to create one.</p>
              </div>
            )}

            {/* ── Report history ──────────────────────────────────── */}
            {selectedUserId && historyItems.length > 1 && (
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                  History ({historyItems.length})
                </p>
                <div className="space-y-1.5">
                  {historyItems.map((item) => {
                    const isJob = 'status' in item;
                    const job = isJob ? item as AIJob : null;
                    const rep = !isJob ? item as PersistedAIReport : null;
                    const itemId = item.id;
                    const isSelected = selectedId === itemId || (!selectedId && effectiveResult?.id === itemId);

                    // Hash freshness badge for persisted reports
                    let hashBadge: React.ReactNode = null;
                    if (rep && currentDataHash) {
                      if (rep.inputHash === currentDataHash) {
                        hashBadge = (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 font-medium shrink-0">
                            current data
                          </span>
                        );
                      } else {
                        hashBadge = (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-50 text-stone-500 border border-stone-200 font-medium shrink-0">
                            outdated
                          </span>
                        );
                      }
                    }

                    return (
                      <button
                        key={itemId}
                        type="button"
                        onClick={() => setSelectedId(itemId)}
                        className={cn(
                          'w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors',
                          isSelected ? 'bg-stone-100 text-stone-900' : 'hover:bg-muted text-muted-foreground',
                        )}
                      >
                        {job && <StatusDot status={job.status} />}
                        {rep && <span className="w-2 h-2 rounded-full bg-stone-400 shrink-0" />}
                        <span className="flex-1 truncate">
                          {job
                            ? `${new Date(job.createdAt).toLocaleDateString()} — job`
                            : `${rep?.period} · ${new Date(rep!.createdAt).toLocaleDateString()} · ${Math.round((rep!.confidenceScore ?? 0) * 100)}%`
                          }
                        </span>
                        {hashBadge}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}
